import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const Z = 10060;
const PAD = 6;
const GAP = 16;
const CARD_W = 320;
const FIND_TIMEOUT_MS = 4000;
// Present but hidden at this screen size, so skip it without the full wait.
const HIDDEN_TIMEOUT_MS = 400;

const isVisible = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

const sameRect = (a, b) =>
  a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

// Puts the card on whichever side of the target has room, in that order.
const placeCard = (rect, card) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampX = (x) => Math.max(16, Math.min(x, vw - card.w - 16));
  const clampY = (y) => Math.max(16, Math.min(y, vh - card.h - 16));
  if (rect.right + GAP + card.w <= vw - 16) {
    const top = clampY(rect.top - 8);
    return { side: "left", left: rect.right + GAP, top, arrow: Math.max(16, Math.min(rect.top + rect.height / 2 - top - 6, card.h - 28)) };
  }
  if (rect.bottom + GAP + card.h <= vh - 16) {
    const left = clampX(rect.left + rect.width / 2 - card.w / 2);
    return { side: "top", left, top: rect.bottom + GAP, arrow: Math.max(16, Math.min(rect.left + rect.width / 2 - left - 6, card.w - 28)) };
  }
  if (rect.top - GAP - card.h >= 16) {
    const left = clampX(rect.left + rect.width / 2 - card.w / 2);
    return { side: "bottom", left, top: rect.top - GAP - card.h, arrow: Math.max(16, Math.min(rect.left + rect.width / 2 - left - 6, card.w - 28)) };
  }
  const top = clampY(rect.top - 8);
  return { side: "right", left: Math.max(16, rect.left - GAP - card.w), top, arrow: 24 };
};

const ARROW_STYLE = {
  left: (at) => ({ left: -7, top: at, borderLeft: "1px solid rgba(160,74,151,0.18)", borderBottom: "1px solid rgba(160,74,151,0.18)" }),
  right: (at) => ({ right: -7, top: at, borderRight: "1px solid rgba(160,74,151,0.18)", borderTop: "1px solid rgba(160,74,151,0.18)" }),
  top: (at) => ({ top: -7, left: at, borderLeft: "1px solid rgba(160,74,151,0.18)", borderTop: "1px solid rgba(160,74,151,0.18)" }),
  bottom: (at) => ({ bottom: -7, left: at, borderRight: "1px solid rgba(160,74,151,0.18)", borderBottom: "1px solid rgba(160,74,151,0.18)" }),
};

const Spotlight = ({ step, index, total, onNext, onBack, onSkip, onMissing }) => {
  const [rect, setRect] = useState(null);
  const [cardSize, setCardSize] = useState({ w: CARD_W, h: 190 });
  const cardRef = useRef(null);
  const targetRef = useRef(null);
  const nextRef = useRef(onNext);
  nextRef.current = onNext;

  // Waits for the target to render (pages load data first), then follows it
  // every frame so scrolling, resizing and animations never leave the hole behind.
  useEffect(() => {
    let frame;
    let cancelled = false;
    let lastRect = null;
    let scrolled = false;
    const started = Date.now();
    targetRef.current = null;
    setRect(null);

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (isVisible(el)) {
        targetRef.current = el;
        if (!scrolled) {
          el.scrollIntoView({ block: "nearest", inline: "nearest" });
          scrolled = true;
        }
        const r = el.getBoundingClientRect();
        const next = { top: r.top, left: r.left, width: r.width, height: r.height };
        if (!sameRect(next, lastRect)) {
          lastRect = next;
          setRect(next);
        }
      } else if (lastRect || Date.now() - started > (el ? HIDDEN_TIMEOUT_MS : FIND_TIMEOUT_MS)) {
        onMissing();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.target]);

  useEffect(() => {
    if (!step.advanceOnTargetClick || !rect) return undefined;
    const el = targetRef.current;
    if (!el) return undefined;
    const advance = () => setTimeout(() => nextRef.current(), 0);
    el.addEventListener("click", advance);
    return () => el.removeEventListener("click", advance);
  }, [step.advanceOnTargetClick, rect]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onSkip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSkip]);

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = cardRef.current;
    if (w !== cardSize.w || h !== cardSize.h) setCardSize({ w, h });
  });

  if (!rect) return null;

  const hole = {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };
  const pos = placeCard({ ...hole, right: hole.left + hole.width, bottom: hole.top + hole.height }, cardSize);
  const isLast = index === total - 1;
  const primaryLabel = step.primary || (isLast ? "Finish" : "Next");
  const handlePrimary = () => {
    if (step.advanceOnTargetClick && targetRef.current) targetRef.current.click();
    else onNext();
  };

  // Four transparent blockers around the hole stop stray clicks on the dimmed
  // page while leaving the highlighted element usable.
  const blockers = [
    { top: 0, left: 0, width: "100vw", height: Math.max(0, hole.top) },
    { top: hole.top + hole.height, left: 0, width: "100vw", bottom: 0 },
    { top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height },
    { top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height },
  ];

  return createPortal(
    <div className="font-outfit" role="dialog" aria-modal="true" aria-labelledby="ds-tour-title">
      {blockers.map((style, i) => (
        <div key={i} style={{ position: "fixed", zIndex: Z, ...style }} />
      ))}
      <div
        style={{
          position: "fixed",
          zIndex: Z,
          ...hole,
          borderRadius: 10,
          pointerEvents: "none",
          boxShadow: "0 0 0 2px #fff, 0 0 0 4px #A04A97, 0 0 0 9999px rgba(20,10,26,0.55)",
          transition: "top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease",
        }}
      />
      <div
        ref={cardRef}
        style={{ position: "fixed", zIndex: Z + 1, top: pos.top, left: pos.left, width: CARD_W, maxWidth: "calc(100vw - 32px)" }}
        className="box-border bg-white rounded-xl border border-[rgba(160,74,151,0.18)] shadow-[0_2px_6px_rgba(24,16,28,0.08),0_24px_50px_-18px_rgba(24,16,28,0.5)] p-4"
      >
        <span
          aria-hidden="true"
          className="absolute w-3 h-3 bg-white rotate-45"
          style={ARROW_STYLE[pos.side](pos.arrow)}
        />
        <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[#A04A97]">
          Step {index + 1} of {total}
        </p>
        <p id="ds-tour-title" className="mt-1.5 text-[16px] font-semibold text-[#18101C]">
          {step.title}
        </p>
        <p className="mt-1.5 text-[13.5px] leading-[1.5] text-[#5B5563]">{step.body}</p>
        <div className="mt-4 flex items-center gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full ${i === index ? "w-[18px] bg-[#A04A97]" : "w-1.5 bg-[rgba(160,74,151,0.25)]"}`}
            />
          ))}
          <span className="flex-1" />
          {index === 0 ? (
            <button type="button" onClick={onSkip} className="px-2 py-1.5 text-[13.5px] font-medium text-[#6B6472] hover:text-[#18101C]">
              Skip tour
            </button>
          ) : (
            <button type="button" onClick={onBack} className="px-2 py-1.5 text-[13.5px] font-medium text-[#6B6472] hover:text-[#18101C]">
              Back
            </button>
          )}
          <button
            type="button"
            onClick={handlePrimary}
            className="px-3.5 py-1.5 rounded-lg bg-[#A04A97] hover:bg-[#8B3F83] text-white text-[13.5px] font-semibold"
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Spotlight;
